// Upsells on the product page (blocks/product-upsells.liquid): the recommendation cards of snippets/upsells.liquid.
// Add buttons and switches add the selected variant in the chosen quantity (the switch removes it again), the
// variant dropdown updates the price, and the carousel moves with arrows and dots. The cart drawer re-renders after
// each change; "After adding: Open the cart drawer" opens it.
(() => {
  if (customElements.get('product-upsells')) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const post = async (path, body) => {
    if (window.theme?.cart?.post) return window.theme.cart.post(path, body);
    const response = await fetch(`${root()}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Theme-Cart': '1', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  };

  class ProductUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.recommend) this.recommend();
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.querySelector('[data-carousel-track]')?.addEventListener('scroll', (event) => this.onScroll(event.currentTarget), { passive: true });
    }

    // Shopify recommendations: the product section rendered for this product with recommendations filled in; this
    // block's cards are taken from it. Nothing shows when Shopify has no recommendations.
    async recommend() {
      const { recommend, limit, productId, sectionId, blockId } = this.dataset;
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=${recommend}`;
      try {
        const html = await fetch(url).then((response) => response.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(`product-upsells[data-block="${blockId}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
        this.querySelector('[data-carousel-track]')?.addEventListener('scroll', (event) => this.onScroll(event.currentTarget), { passive: true });
      } catch (error) {
        // Keep what is shown.
      }
    }

    // Every change goes through the cart drawer when it is on the page, so it re-renders (count, lines).
    async cart(task, opened) {
      const drawer = document.querySelector('cart-drawer');
      const message = this.querySelector('[data-upsells-message]');
      message.hidden = true;
      try {
        if (drawer?.active) await drawer.run(task);
        else await task();
        if (opened) {
          if (this.dataset.afterAdd === 'drawer' && drawer?.active) drawer.open();
          else if (this.dataset.afterAdd === 'drawer') window.location.href = `${root()}cart`;
          else this.say(this.dataset.added);
        }
        document.dispatchEvent(new CustomEvent('cart:added'));
      } catch (error) {
        this.say(error.message, true);
      }
    }

    say(text, error = false) {
      const message = this.querySelector('[data-upsells-message]');
      if (!text) return;
      message.textContent = text;
      message.toggleAttribute('data-error', error);
      message.hidden = false;
      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => { message.hidden = true; }, 4000);
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target) return;
      const card = target.closest('[data-card]');
      const quantity = () => Number(card?.querySelector('[data-card-quantity]')?.value) || 1;

      if (target.matches('[data-qty-step]')) {
        const input = target.parentElement.querySelector('input');
        input.value = Math.max(1, (Number(input.value) || 1) + Number(target.dataset.qtyStep));
        this.followQuantity(card, Number(input.value));
        return;
      }
      if (target.matches('[data-card-add]') && card) {
        target.setAttribute('aria-busy', 'true');
        this.cart(() => post('cart/add.js', { items: [{ id: Number(card.dataset.variantId), quantity: quantity() }] }), true)
          .finally(() => target.removeAttribute('aria-busy'));
        return;
      }
      if (target.matches('[data-card-toggle]') && card) {
        const on = target.getAttribute('aria-checked') === 'true';
        target.setAttribute('aria-checked', String(!on));
        this.cart(() => (on
          ? post('cart/change.js', { id: String(card.dataset.variantId), quantity: 0 })
          : post('cart/add.js', { items: [{ id: Number(card.dataset.variantId), quantity: quantity() }] })), !on);
        return;
      }
      if (target.matches('[data-carousel-step]')) {
        const track = this.querySelector('[data-carousel-track]');
        track.scrollBy({ left: track.clientWidth * Number(target.dataset.carouselStep), behavior: 'smooth' });
        return;
      }
      if (target.closest('[data-carousel-dots]')) {
        const track = this.querySelector('[data-carousel-track]');
        const index = [...target.parentElement.children].indexOf(target);
        track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
      }
    }

    // A card whose switch is on is in the cart: its quantity follows the card's quantity selector.
    followQuantity(card, quantity) {
      if (card?.querySelector('[data-card-toggle]')?.getAttribute('aria-checked') !== 'true') return;
      clearTimeout(card.quantityTimer);
      card.quantityTimer = setTimeout(() => this.cart(() => post('cart/change.js', { id: String(card.dataset.variantId), quantity })), 300);
    }

    onChange(event) {
      const input = event.target;
      const card = input.closest('[data-card]');
      if (input.matches('[data-card-quantity]')) {
        input.value = Math.max(1, Number(input.value) || 1);
        this.followQuantity(card, Number(input.value));
      }
      if (input.matches('[data-variant-select]') && card) {
        const option = input.selectedOptions[0];
        card.dataset.variantId = input.value;
        card.querySelector('[data-card-price]').textContent = option.dataset.price;
        const compare = card.querySelector('[data-card-compare]');
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }
    }

    onScroll(track) {
      const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      this.querySelectorAll('[data-carousel-dots] button').forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }
  }

  customElements.define('product-upsells', ProductUpsells);
})();
